import { useLayoutEffect, useRef, useState } from 'react'
import { ToolIcons } from './toolIcons'
import { DRILL_DOWN_ICON_PATH } from './Toolbar'
import { createPortal } from 'react-dom'
import { usePortalDismiss } from '../hooks/usePortalDismiss'
import { MenuRow, Submenu, Divider } from './contextMenuChrome'
import { CHART_TYPES, chartTypeIcon, type ChartTypeEntry } from './chartTypes'
import { BetaTag } from './BetaTag'
import { styleGroupOf } from './chartSettings'
import { OVERLAY_GROUPS } from '../utils/overlays'
import { TOOL_HOTKEYS } from './toolHotkeys'
import { isMacPlatform } from '../utils/platform'
import { Z_MODAL } from './zLayers'

/* ------------------------------------------------------------------ */
/*  Chart-body right-click menu                                         */
/* ------------------------------------------------------------------ */

/**
 * The chart BODY (candles) right-click menu — the fast path into view, drawing,
 * indicator, chart-type and settings actions the chart already owns, plus our
 * differentiators (replay / ask-AI / drill) scoped to the trading day under the
 * cursor. Sibling to `PriceScaleContextMenu`: same portal / clamp / dismiss
 * shell and the shared `contextMenuChrome` rows.
 *
 * Every parity action is a handler the host (EdgeChart) already has; the two
 * "…this day" host actions (`onReplayFromDay`, `onAskAiAboutDay`) are OPT-IN —
 * omit them and the rows don't render (keeps the engine free of app deps). The
 * "…this day" rows also require a resolvable bar under the cursor
 * (`hasBarContext`); the drill row is additionally daily-only.
 */
export interface ChartBodyContextMenuProps {
  /** Viewport coordinates of the right-click that opened the menu. */
  x: number
  y: number
  /** Active chart type — marks the current row in the Chart Type submenu. */
  chartType: string
  /** Chart types to list. Defaults to the full catalog; Beta entries carry a chip. */
  chartTypes?: readonly ChartTypeEntry[]
  /** Live indicator hidden state — flips the toggle row's label. */
  indicatorsHidden: boolean
  /** A bar timestamp is resolvable under the cursor — gates the "…this day" rows. */
  hasBarContext: boolean

  // Opt-in host actions (omit to hide the row):
  /** Tag the ticker on the trading day under the cursor. Host owns the picker —
   *  the engine stays free of any tag dependency. */
  onTagDay?: () => void
  /** "Add alert at $X": open the host's price alert popover at the price under the cursor.
   *  Renders only with `addAlertPrice`. */
  onAddAlert?: () => void
  /** That price at the chart's precision ("4.52"). */
  addAlertPrice?: string
  /** Replay the trading day under the cursor. */
  onReplayFromDay?: () => void
  /** Open the AI Companion scoped to the day under the cursor. */
  onAskAiAboutDay?: () => void
  /** Drill on the day under the cursor. Meaning is host-defined (dashboard/
   *  scanner: daily → intraday; history: open the event drill overlay). The row
   *  shows whenever this is wired + a bar is under the cursor. */
  onDrillIntoDay?: () => void
  /** Exclude this chart's ticker from whatever scan populated it (Chart
   *  Scanner only). Unlike the "…this day" rows, this is NOT gated on
   *  `hasBarContext` — it targets the whole ticker, not a specific bar. */
  onExcludeTicker?: () => void
  /** Ticker symbol shown in the exclude row's label ("Exclude {symbol}").
   *  Required whenever `onExcludeTicker` is wired. */
  excludeTickerLabel?: string
  /** Pattern Explorer: find historical days whose intraday shape resembles the day under the
   *  cursor. Host owns the (ticker, day) context + window; the engine stays pattern-agnostic.
   *  Rendered as the TOP item — it's a dedicated, deliberate action, not an everyday control.
   *  `scopeId` is the chosen peer universe (see `findSimilarScopes`); '' when no scope list. */
  onFindSimilarPattern?: (scopeId: string) => void
  /** Scope options for "Find similar", as a grouped list of section headers + clickable items (e.g.
   *  a "Compare To" header, "Same Ticker", a "History Presets" header, then presets). When present,
   *  the row becomes a submenu; the host maps the picked `id` to a peer cohort. Engine stays agnostic. */
  findSimilarScopes?: Array<{ header: string } | { id: string; label: string }>
  /** Beta auto-analysis quick toggles. `available` mirrors the Settings-modal
   *  Beta gates: a row renders only for a feature the user has opted into, and
   *  the submenu itself only when at least one is available. `sr` / `fib` /
   *  `trend` are the live on/off states shown as check glyphs. */
  autoAnalysis?: {
    sr: boolean
    fib: boolean
    trend: boolean
    available: { sr: boolean; fib: boolean; trend: boolean }
  }
  onToggleAutoAnalysis?: (key: AutoAnalysisKey) => void
  /** Optional host-owned transaction trace toggle. Omit the callback to hide it. */
  autoTracesEnabled?: boolean
  onToggleAutoTraces?: () => void

  // Engine-internal actions (always present):
  onResetView: () => void
  /** Restore default zoom (bar space) + scroll to real time — the escape
   *  hatch a rubber-band-zoom drag removes the obvious way back out of. */
  onResetZoom: () => void
  onDrawLine: (overlayName: string) => void
  onRemoveDrawings: () => void
  onToggleIndicators: () => void
  onChartType: (type: string) => void
  onSettings: () => void
  onClose: () => void
}

export type AutoAnalysisKey = 'sr' | 'fib' | 'trend'

const AUTO_ANALYSIS_ROWS: Array<{ key: AutoAnalysisKey; label: string }> = [
  { key: 'sr', label: 'Support / resistance' },
  { key: 'fib', label: 'Auto Fibonacci' },
  { key: 'trend', label: 'Auto trendlines' },
]

// "Draw Line ›" reuses the canonical "Lines" overlay group (the DrawingBar's
// first group) so the menu and the drawing bar never drift on tool names.
const LINE_TOOLS = OVERLAY_GROUPS[0]?.tools ?? []

/* ------------------------------------------------------------------ */
/*  Row icons                                                          */
/* ------------------------------------------------------------------ */
/**
 * One glyph per action, rendered in the row's existing leading column — the
 * slot the check/radio state glyphs already reserve, so labels stay aligned and
 * the menu gains no width. Stroke-based at 12px to match the chevron and the
 * rest of the chart chrome.
 */
const I = {
  width: 12,
  height: 12,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

const MenuIcons = {
  reset: (
    <svg {...I}>
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  ),
  resetZoom: (
    <svg {...I}>
      <circle cx="10" cy="10" r="7" />
      <line x1="7" y1="10" x2="13" y2="10" />
      <line x1="21" y1="21" x2="15" y2="15" />
    </svg>
  ),
  drawLine: (
    <svg {...I}>
      <path d="M4 20L20 4" />
    </svg>
  ),
  tag: (
    <svg {...I}>
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  ),
  replay: (
    <svg {...I}>
      <polygon points="5 3 19 12 5 21 5 3" fill="currentColor" stroke="none" />
    </svg>
  ),
  askAi: (
    <svg {...I}>
      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" />
      <path d="M18.5 16.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
    </svg>
  ),
  // Same glyph as the on-chart drill-down button (Toolbar's cornerLeftDown) so
  // the two drill affordances read identically — sharing one path constant.
  drill: (
    <svg {...I}>
      <path d={DRILL_DOWN_ICON_PATH} />
    </svg>
  ),
  // sparkline with a fitted line through it — analysis the chart draws itself
  autoAnalysis: (
    <svg {...I}>
      <path d="M3 17l5-6 4 3 5-8 4 5" />
      <path d="M3 20h18" strokeDasharray="3 2" />
    </svg>
  ),
  autoTraces: (
    <svg {...I}>
      <path d="M5 17L19 7" />
      <circle cx="5" cy="17" r="2" />
      <circle cx="19" cy="7" r="2" />
    </svg>
  ),
  // lucide bell-plus: a price alert, added
  addAlert: (
    <svg {...I}>
      <path d="M10.268 21a2 2 0 0 0 3.464 0" />
      <path d="M15 8h6" />
      <path d="M18 5v6" />
      <path d="M20.002 14.464a9 9 0 0 0 .738.863A1 1 0 0 1 20 17H4a1 1 0 0 1-.74-1.673C4.59 13.956 6 12.499 6 8a6 6 0 0 1 8.75-5.332" />
    </svg>
  ),
  findSimilar: (
    // magnifier over a small waveform — "search by shape"
    <svg {...I}>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-3.5-3.5" />
      <path d="M8 12l2-3 2 4 2-2" />
    </svg>
  ),
  removeDrawings: (
    <svg {...I}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  ),
  exclude: (
    <svg {...I}>
      <circle cx="12" cy="12" r="9" />
      <line x1="6" y1="18" x2="18" y2="6" />
    </svg>
  ),
  indicatorsShown: (
    <svg {...I}>
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  indicatorsHidden: (
    <svg {...I}>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ),
  chartType: (
    <svg {...I}>
      <path d="M7 4v3M7 17v3M7 7h0M17 4v5M17 15v5" />
      <rect x="4.5" y="7" width="5" height="10" rx="1" />
      <rect x="14.5" y="9" width="5" height="6" rx="1" />
    </svg>
  ),
  settings: (
    <svg {...I}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6h.09A1.65 1.65 0 0 0 10 3.09V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
}

/**
 * A drawing tool's own glyph, reused from the toolbar so the same tool never
 * wears two faces. The artwork is drawn at 22px and the menu's icon column is
 * 14px — CSS width/height beat an SVG's presentation attributes, so scaling
 * here needs no second copy.
 */
const toolIcon = (name: string) =>
  ToolIcons[name] ? (
    <span className="flex [&>svg]:h-3.5 [&>svg]:w-3.5">{ToolIcons[name]}</span>
  ) : undefined

/**
 * A chart type's own glyph, from the same `chartTypeIcon` path the toolbar
 * dropdown and the panel-header pill draw — so a chart type never wears two
 * faces across the four surfaces that list them.
 */
const typeIcon = (name: string) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-3.5 w-3.5"
    aria-hidden
  >
    <path d={chartTypeIcon(name)} />
  </svg>
)

export function ChartBodyContextMenu({
  x,
  y,
  chartType,
  chartTypes = CHART_TYPES,
  indicatorsHidden,
  hasBarContext,
  onTagDay,
  onAddAlert,
  addAlertPrice,
  onExcludeTicker,
  excludeTickerLabel,
  onFindSimilarPattern,
  findSimilarScopes,
  onReplayFromDay,
  onAskAiAboutDay,
  onDrillIntoDay,
  onResetView,
  onResetZoom,
  onDrawLine,
  onRemoveDrawings,
  onToggleIndicators,
  autoAnalysis,
  onToggleAutoAnalysis,
  autoTracesEnabled = false,
  onToggleAutoTraces,
  onChartType,
  onSettings,
  onClose,
}: ChartBodyContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)

  // Two-pass placement: render at the cursor, clamp to the viewport once the
  // menu's measured size is known. Hidden until measured (same as the price menu).
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)
  useLayoutEffect(() => {
    const el = menuRef.current
    if (!el) return
    const margin = 8
    const w = el.offsetWidth
    const h = el.offsetHeight
    const left = Math.max(margin, Math.min(x, window.innerWidth - w - margin))
    const top = Math.max(margin, Math.min(y, window.innerHeight - h - margin))
    setPos({ left, top })
  }, [x, y])

  usePortalDismiss(menuRef, onClose)

  // Each row runs its action then closes the menu. Submenu rows do the same.
  const run = (fn?: () => void) => () => {
    fn?.()
    onClose()
  }

  const showFindSimilar = !!onFindSimilarPattern && hasBarContext
  const showTag = !!onTagDay && hasBarContext
  const showReplay = !!onReplayFromDay && hasBarContext
  const showAskAi = !!onAskAiAboutDay && hasBarContext
  const showDrill = !!onDrillIntoDay && hasBarContext
  const showDayGroup = showTag || showReplay || showAskAi || showDrill
  // Not gated on hasBarContext — this targets the whole ticker, not a
  // specific bar under the cursor.
  const showExclude = !!onExcludeTicker
  const showAddAlert = !!onAddAlert && !!addAlertPrice

  const menu = (
    <div
      ref={menuRef}
      data-chart-popup=""
      role="menu"
      className="fixed flex min-w-[220px] flex-col rounded-lg border border-primary/15 bg-popover py-1 text-[hsl(var(--chart-text))] shadow-2xl shadow-black/50"
      style={{
        zIndex: Z_MODAL,
        left: pos?.left ?? x,
        top: pos?.top ?? y,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {showFindSimilar && (
        <>
          {findSimilarScopes && findSimilarScopes.length > 0 ? (
            <Submenu label="Find similar pattern" icon={MenuIcons.findSimilar}>
              {findSimilarScopes.map((item, i) =>
                'header' in item ? (
                  <div
                    key={`h-${i}`}
                    role="presentation"
                    className="px-2.5 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--chart-text-muted))]"
                  >
                    {item.header}
                  </div>
                ) : (
                  <MenuRow
                    key={item.id}
                    glyph={null}
                    icon={null}
                    onSelect={run(() => onFindSimilarPattern!(item.id))}
                  >
                    {item.label}
                  </MenuRow>
                ),
              )}
            </Submenu>
          ) : (
            <MenuRow
              glyph={null}
              icon={MenuIcons.findSimilar}
              onSelect={run(() => onFindSimilarPattern!(''))}
            >
              Find similar pattern…
            </MenuRow>
          )}
          <Divider />
        </>
      )}

      {showAddAlert && (
        <>
          <MenuRow glyph={null} icon={MenuIcons.addAlert} onSelect={run(onAddAlert)}>
            Add alert at ${addAlertPrice}
          </MenuRow>
          <Divider />
        </>
      )}

      <MenuRow glyph={null} icon={MenuIcons.reset} onSelect={run(onResetView)}>
        Reset Chart View
      </MenuRow>
      <MenuRow glyph={null} icon={MenuIcons.resetZoom} onSelect={run(onResetZoom)}>
        Reset zoom
      </MenuRow>

      <Submenu label="Draw Line" icon={MenuIcons.drawLine}>
        {LINE_TOOLS.map((tool) => (
          <MenuRow
            key={tool.name}
            glyph={null}
            icon={toolIcon(tool.name)}
            hotkey={TOOL_HOTKEYS[tool.name]}
            onSelect={run(() => onDrawLine(tool.name))}
          >
            {tool.label}
          </MenuRow>
        ))}
      </Submenu>

      {showDayGroup && (
        <>
          <Divider />
          {/* First in the group: tagging is the highest-frequency day action,
              and this row is the discoverable twin of the `t` shortcut. */}
          {showTag && (
            <MenuRow
              glyph={null}
              icon={MenuIcons.tag}
              hotkey={isMacPlatform() ? '⌥T' : 'Alt+T'}
              onSelect={run(onTagDay)}
            >
              Tag this day…
            </MenuRow>
          )}
          {showReplay && (
            <MenuRow glyph={null} icon={MenuIcons.replay} onSelect={run(onReplayFromDay)}>
              Replay from this day
            </MenuRow>
          )}
          {showAskAi && (
            <MenuRow glyph={null} icon={MenuIcons.askAi} onSelect={run(onAskAiAboutDay)}>
              Ask AI about this day
            </MenuRow>
          )}
          {showDrill && (
            <MenuRow glyph={null} icon={MenuIcons.drill} onSelect={run(onDrillIntoDay)}>
              Drill into this day
            </MenuRow>
          )}
        </>
      )}

      {showExclude && (
        <>
          <Divider />
          <MenuRow glyph={null} icon={MenuIcons.exclude} onSelect={run(onExcludeTicker)}>
            Exclude {excludeTickerLabel}
          </MenuRow>
        </>
      )}

      <Divider />

      <MenuRow glyph={null} icon={MenuIcons.removeDrawings} onSelect={run(onRemoveDrawings)}>
        Remove Drawings
      </MenuRow>
      <MenuRow
        glyph={null}
        icon={indicatorsHidden ? MenuIcons.indicatorsHidden : MenuIcons.indicatorsShown}
        hotkey={isMacPlatform() ? '⌥S' : 'Alt+S'}
        onSelect={run(onToggleIndicators)}
      >
        {indicatorsHidden ? 'Show indicators' : 'Hide indicators'}
      </MenuRow>
      {onToggleAutoTraces && (
        <MenuRow
          glyph={null}
          icon={MenuIcons.autoTraces}
          trailingGlyph={autoTracesEnabled ? 'check' : null}
          onSelect={run(onToggleAutoTraces)}
        >
          Auto Traces
        </MenuRow>
      )}
      {autoAnalysis &&
        onToggleAutoAnalysis &&
        AUTO_ANALYSIS_ROWS.some((r) => autoAnalysis.available[r.key]) && (
          <Submenu label="Auto analysis" icon={MenuIcons.autoAnalysis}>
            {AUTO_ANALYSIS_ROWS.filter((r) => autoAnalysis.available[r.key]).map((r) => (
              <MenuRow
                key={r.key}
                glyph={autoAnalysis[r.key] ? 'check' : null}
                onSelect={run(() => onToggleAutoAnalysis(r.key))}
              >
                {r.label}
              </MenuRow>
            ))}
          </Submenu>
        )}

      <Divider />

      <Submenu label="Chart Type" icon={MenuIcons.chartType}>
        {chartTypes.map((ct) => {
          // Family match, not string equality. `candle_stroke` and `area` are
          // still valid persisted types (they are what the hollow / area-fill
          // modifiers write), so a chart on either must still mark its family
          // active here — and re-picking that family must NOT reset the modifier
          // back to solid or unfilled. Mirrors the toolbar dropdown.
          const active = styleGroupOf(chartType) === styleGroupOf(ct.name)
          return (
            <MenuRow
              key={ct.name}
              // Icon leads, selected state trails — the same arrangement as the
              // toolbar dropdown, rather than a radio replacing the icon.
              glyph={null}
              icon={typeIcon(ct.name)}
              trailingGlyph={active ? 'radio' : null}
              onSelect={run(() => {
                if (!active) onChartType(ct.name)
              })}
            >
              {active ? <span className="text-primary">{ct.label}</span> : ct.label}
              {ct.beta && <BetaTag className="ml-1.5" />}
            </MenuRow>
          )
        })}
      </Submenu>
      <MenuRow glyph={null} icon={MenuIcons.settings} onSelect={run(onSettings)}>
        Settings
      </MenuRow>
    </div>
  )

  return createPortal(menu, document.body)
}
